// Communications tab for the request detail modal (Phase 1).
// Phase 2 will add summary panel and scoped chat drawer to this same file.
//
// Notes vs original Phase 1 prompt:
//   - Inp/Sel onChange takes (v: string) => void, NOT a React event handler
//   - Sel uses an options={[{value,label}]} prop, NOT JSX <option> children
//   - apiCall auto-prepends /api, so call paths start at /requests, /threads, /messages
//   - All T.* theme keys verified against frontend/src/theme.ts

import { useState, useEffect, useCallback, useRef } from "react";
import { T } from "../theme";
import { fmtDate, hasPermission } from "../utils";
import { ensureStreamSession } from "../streamSession";
import { apiCall, apiCallEx } from "../apiClient";
import { Btn, Card, Modal, Inp, Sel, EmptyState } from "./primitives";
import { OrgBadge } from "./OrgBadge";
import type { MessageThread, Message, SubscriptionState, ThreadType, ThreadSummary } from "../types/communications";
import type { UserRow } from "../types/api";

// Phase 1.5 types
interface ThreadUser {
  id: string;
  name: string;
  email: string;
  role_slug: string;
}

interface MentionWarning {
  type: string;
  mentioned_user_id: string;
  mentioned_user_name: string;
  thread_id: string;
  message: string;
}

// Phase 1.5: simple toast for mention warnings
const Toast = ({ message, onDismiss }: { message: string; onDismiss: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 8000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div style={{
      position: "fixed",
      top: 20,
      right: 20,
      padding: "12px 20px",
      background: T.ylwS,
      border: `2px solid ${T.ylw}`,
      borderRadius: 6,
      fontSize: 12,
      color: T.tx,
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      zIndex: 10000,
      maxWidth: 400,
      cursor: "pointer",
    }} onClick={onDismiss}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Notice</div>
      <div>{message}</div>
    </div>
  );
};

interface CommunicationsTabProps {
  requestId: string;
  currentUser: UserRow | null;
  onUnreadCountChange?: (count: number) => void;
  onNavigateToVersions?: () => void;
  // When set, the first loadThreads() call will pre-select this thread if it
  // exists. Only honored on first render — manual thread selection wins after.
  initialThreadId?: string | null;
}

const THREAD_TYPE_LABELS: Record<ThreadType, string> = {
  general: "General",
  intake_scoping: "Intake and Scoping",
  commops_review: "CommOps Review",
  legal_review: "Legal Review",
  internal_review: "Internal Review",
  counterparty_correspondence: "Counterparty Correspondence",
  executive_briefing: "Executive Briefing",
  ad_hoc: "Custom",
};

export const CommunicationsTab = ({ requestId, currentUser, onUnreadCountChange, onNavigateToVersions, initialThreadId = null }: CommunicationsTabProps) => {
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [chatDrawerOpen, setChatDrawerOpen] = useState<boolean>(false);
  const [showSendModal, setShowSendModal] = useState<boolean>(false);
  // initialThreadId is honored once on first loadThreads. We capture the
  // first non-null prop value into a sticky ref so a parent re-render that
  // clears the prop (RequestsView clears pendingSelectThreadId after the
  // RequestDetailModal mount effect) does NOT race ahead of CommunicationsTab
  // mounting (CommunicationsTab only mounts when tab=='communications').
  const stickyInitialThreadIdRef = useRef<string | null>(initialThreadId || null);
  if (initialThreadId && !stickyInitialThreadIdRef.current) {
    stickyInitialThreadIdRef.current = initialThreadId;
  }
  const initialThreadConsumedRef = useRef<boolean>(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    const res = await apiCall(`/requests/${requestId}/threads`);
    if (res && Array.isArray(res.threads)) {
      setThreads(res.threads);
      if (!selectedThreadId && res.threads.length > 0) {
        const target = stickyInitialThreadIdRef.current;
        const preferred = !initialThreadConsumedRef.current && target
          ? res.threads.find((t: MessageThread) => t.id === target)
          : null;
        setSelectedThreadId(preferred ? preferred.id : res.threads[0].id);
        initialThreadConsumedRef.current = true;
      }
      const totalUnread = res.threads.reduce((sum: number, t: MessageThread) => sum + (t.unread_count || 0), 0);
      if (onUnreadCountChange) onUnreadCountChange(totalUnread);
    }
    setLoading(false);
  }, [requestId, selectedThreadId, onUnreadCountChange]);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  const selectedThread = threads.find(t => t.id === selectedThreadId) || null;
  // Inline check is tighter than messages.create_thread (which includes
  // business_reviewer + requester by default) — preserving existing gate.
  const canCreateThread = currentUser && ['admin', 'lawyer', 'legalops', 'commercial_ops', 'contract_manager'].includes(currentUser.role || '');
  const canSendToCounterparty = hasPermission(currentUser, 'comms.send_to_counterparty') || (currentUser && ['admin', 'lawyer', 'legalops', 'commercial_ops', 'contract_manager', 'requester'].includes(currentUser.role || ''));

  if (loading) {
    return <div style={{ padding: 20, textAlign: "center", color: T.txM }}>Loading communications...</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10, gap: 8 }}>
        {canSendToCounterparty && (
          <Btn v="primary" sz="sm" onClick={() => setShowSendModal(true)}>Send to counterparty</Btn>
        )}
        <Btn v="secondary" sz="sm" onClick={() => setChatDrawerOpen(true)}>Ask about this request</Btn>
      </div>
      <div style={{ display: "flex", gap: 16, minHeight: 500 }}>
      <div style={{ width: 280, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Threads</div>
          {canCreateThread ? (
            <Btn v="ghost" sz="sm" onClick={() => setShowCreateModal(true)}>+ New</Btn>
          ) : null}
        </div>
        {threads.length === 0 ? (
          <EmptyState msg="No threads yet" />
        ) : (
          <div>
            {threads.map(t => (
              <ThreadListItem
                key={t.id}
                thread={t}
                isActive={t.id === selectedThreadId}
                onClick={() => setSelectedThreadId(t.id)}
              />
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {selectedThread ? (
          <ThreadView
            thread={selectedThread}
            currentUser={currentUser}
            onMessagePosted={() => { void loadThreads(); }}
            onShowToast={(msg: string) => setToastMessage(msg)}
            onNavigateToVersions={onNavigateToVersions}
          />
        ) : (
          <EmptyState msg="Select a thread to view messages" />
        )}
      </div>

      {showCreateModal && (
        <AdHocThreadCreateModal
          requestId={requestId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); void loadThreads(); }}
        />
      )}

      </div>
      <ScopedChatDrawer
        requestId={requestId}
        selectedThreadId={selectedThreadId}
        currentUserId={currentUser?.id || null}
        open={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
      />
      {toastMessage && <Toast message={toastMessage} onDismiss={() => setToastMessage(null)} />}
      {showSendModal && (
        <SendToCounterpartyModal
          requestId={requestId}
          onClose={() => setShowSendModal(false)}
          onSuccess={(emails: string[]) => {
            setToastMessage("Sent to " + emails.join(", "));
            void loadThreads();
          }}
          onError={(errMsg: string) => setToastMessage("Send failed: " + errMsg)}
        />
      )}
    </div>
  );
};

const ThreadListItem = ({ thread, isActive, onClick }: { thread: MessageThread; isActive: boolean; onClick: () => void }) => {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "10px 12px",
        marginBottom: 4,
        borderRadius: 6,
        border: "none",
        background: isActive ? T.accS : "transparent",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? T.acc : T.tx }}>
          {thread.title}
        </div>
        {thread.unread_count && thread.unread_count > 0 ? (
          <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 8, background: T.red, color: "#fff", fontWeight: 700 }}>
            {thread.unread_count}
          </span>
        ) : null}
      </div>
      <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>
        {THREAD_TYPE_LABELS[thread.thread_type] || thread.thread_type}
        {thread.status === 'closed' && ' · Closed'}
      </div>
    </button>
  );
};

const SUBSCRIPTION_OPTIONS = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'mention_only', label: 'Mentions only' },
  { value: 'unsubscribed', label: 'Muted' },
];

interface CompanyDraftVersion {
  id: string;
  filename: string;
  version_number: number;
  label: string;
}

interface CounterpartyContext {
  request_title: string;
  counterparty: string;
  company_entity_name: string;
  agreement_type: string;
  preset_to: string[];
  prior_recipients: string[];
}

interface SendToCounterpartyModalProps {
  requestId: string;
  onClose: () => void;
  onSuccess: (toEmails: string[]) => void;
  onError: (errorMsg: string) => void;
}

export const SendToCounterpartyModal = ({ requestId, onClose, onSuccess, onError }: SendToCounterpartyModalProps) => {
  const [attachDraft, setAttachDraft] = useState<boolean>(true);
  const [companyDraft, setCompanyDraft] = useState<CompanyDraftVersion | null>(null);
  const [context, setContext] = useState<CounterpartyContext | null>(null);
  const [loadingDraft, setLoadingDraft] = useState<boolean>(true);

  const [toInput, setToInput] = useState<string>("");
  const [ccInput, setCcInput] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [bodyText, setBodyText] = useState<string>("");

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Owner ruling 2026-07-14: comment warn-and-confirm. When the server blocks
  // with the 409 confirmation contract (Word comments found in the outgoing
  // draft, or the file could not be verified), the details land here and the
  // Send button becomes an explicit "Send anyway". Tracked changes never warn.
  const [commentWarning, setCommentWarning] = useState<{ error: string; count: number; comments: Array<{ author: string | null; snippet: string }>; reason: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingDraft(true);
      const [draftRes, contextRes] = await Promise.all([
        apiCall("/requests/" + requestId + "/latest-company-draft"),
        apiCall("/requests/" + requestId + "/counterparty-context"),
      ]);
      if (cancelled) return;

      let hasDraft = false;
      if (draftRes && draftRes.version) {
        setCompanyDraft(draftRes.version as CompanyDraftVersion);
        hasDraft = true;
      } else {
        setAttachDraft(false);
      }

      if (contextRes && contextRes.request_title !== undefined) {
        const ctx = contextRes as CounterpartyContext;
        setContext(ctx);
        setSubject("Agreement Review: " + ctx.company_entity_name + " and " + (ctx.counterparty || "the counterparty") + " - " + ctx.agreement_type);
        if (hasDraft) {
          setBodyText("Please find the attached draft for your review. Let us know if you have questions.");
          setToInput((ctx.preset_to || []).join(", "));
        } else {
          setToInput((ctx.prior_recipients || []).join(", "));
        }
      }
      setLoadingDraft(false);
    };
    void load();
    return () => { cancelled = true; };
  }, [requestId]);

  useEffect(() => {
    if (!context) return;
    if (attachDraft) {
      setBodyText(function(prev) { return prev || "Please find the attached draft for your review. Let us know if you have questions."; });
      if (!toInput.trim()) setToInput((context.preset_to || []).join(", "));
    } else {
      setBodyText("");
      if (!toInput.trim() || toInput === (context.preset_to || []).join(", ")) {
        setToInput((context.prior_recipients || []).join(", "));
      }
    }
  }, [attachDraft]);

  const parseEmails = (input: string): string[] => {
    return input.split(/[,;]/).map(function(s) { return s.trim(); }).filter(function(s) { return s.length > 0; });
  };

  const validate = (): string | null => {
    if (attachDraft && !companyDraft) return "No company draft available to attach.";
    const toList = parseEmails(toInput);
    if (toList.length === 0) return "At least one recipient is required.";
    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    for (var i = 0; i < toList.length; i++) {
      if (!emailRegex.test(toList[i])) return "Invalid email format: " + toList[i];
    }
    const ccList = parseEmails(ccInput);
    for (var j = 0; j < ccList.length; j++) {
      if (!emailRegex.test(ccList[j])) return "Invalid CC email: " + ccList[j];
    }
    if (!subject.trim()) return "Subject is required.";
    return null;
  };

  const handleSubmit = async (confirmedComments = false) => {
    const err = validate();
    if (err) { setValidationError(err); return; }
    setValidationError(null);
    setSubmitting(true);
    const toList = parseEmails(toInput);
    const ccList = parseEmails(ccInput);
    const res = await apiCallEx("/requests/" + requestId + "/send-to-counterparty", {
      method: "POST",
      body: JSON.stringify({
        versionId: attachDraft && companyDraft ? companyDraft.id : null,
        to: toList, cc: ccList, subject: subject.trim(), bodyText: bodyText.trim(), attachDraft: attachDraft,
        ...(confirmedComments ? { confirmedComments: true } : {}),
      }),
    });
    setSubmitting(false);
    if (res.ok && res.data && res.data.ok) { onSuccess(toList); onClose(); return; }
    if (res.status === 409 && res.data && res.data.requiresConfirmation) {
      // Comments found (or unverifiable) — show the warning; the next submit
      // carries confirmedComments and the file is sent exactly as stored.
      setCommentWarning({ error: res.data.error, count: res.data.count || 0, comments: res.data.comments || [], reason: res.data.reason || null });
      return;
    }
    setCommentWarning(null);
    onError((res.data && res.data.error) ? res.data.error : "Failed to send. Please try again.");
  };

  const handleTrackedDownload = () => {
    if (!companyDraft) return;
    const token = localStorage.getItem("kontractai_token") || "";
    void (async () => {
      try {
        const resp = await fetch("/api/versions/" + companyDraft.id + "/download-tracked", {
          headers: { Authorization: "Bearer " + token },
        });
        if (!resp.ok) { onError("Download failed: HTTP " + resp.status); return; }
        const blob = await resp.blob();
        const disp = resp.headers.get("content-disposition") || "";
        const match = disp.match(/filename\*?=(?:UTF-8'')?\"?([^";]+)\"?/i);
        const fname = match ? decodeURIComponent(match[1]) : companyDraft.filename;
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl; a.download = fname;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch (e) { onError("Download failed: " + (e as Error).message); }
    })();
  };

  return (
    <Modal open onClose={onClose} title={attachDraft ? "Send draft to counterparty" : "Send follow-up to counterparty"}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 560 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: T.bg3, border: "1px solid " + T.brd, borderRadius: 6 }}>
          <input type="checkbox" id="attachDraftToggle" checked={attachDraft} onChange={function(e) { setAttachDraft(e.target.checked); setCommentWarning(null); }} disabled={!companyDraft} style={{ cursor: companyDraft ? "pointer" : "not-allowed" }} />
          <label htmlFor="attachDraftToggle" style={{ fontSize: 12, color: T.tx, cursor: companyDraft ? "pointer" : "not-allowed" }}>Attach latest company draft</label>
          {!companyDraft && <span style={{ fontSize: 10, color: T.txM, fontStyle: "italic" }}>(no company draft available - text-only mode)</span>}
        </div>

        {attachDraft && companyDraft && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.tx, marginBottom: 4 }}>Draft to send (locked: latest company draft only)</div>
            <div style={{ padding: "10px 12px", background: T.bg3, border: "1px solid " + T.brd, borderRadius: 6, fontSize: 12, color: T.tx }}>
              v{companyDraft.version_number} — company_draft — {companyDraft.filename}
            </div>
            <div style={{ marginTop: 6 }}>
              <button type="button" onClick={handleTrackedDownload} style={{ background: "none", border: "1px solid " + T.brd, borderRadius: 4, padding: "4px 10px", fontSize: 10, color: T.txM, cursor: "pointer", fontFamily: "inherit" }} title="Download with embedded identifier so replies are still matched if you send via Outlook.">
                Download for Outlook (tracked)
              </button>
            </div>
          </div>
        )}

        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.tx, marginBottom: 4 }}>To (comma-separated for multiple) <span style={{ color: T.red }}>*</span></div>
          <Inp value={toInput} onChange={function(v: string) { setToInput(v); setValidationError(null); }} placeholder="counsel@counterparty.com, partner@counterparty.com" />
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.tx, marginBottom: 4 }}>CC (optional, comma-separated)</div>
          <Inp value={ccInput} onChange={function(v: string) { setCcInput(v); setValidationError(null); }} placeholder="colleague@example.com" />
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.tx, marginBottom: 4 }}>Subject <span style={{ color: T.red }}>*</span></div>
          <Inp value={subject} onChange={function(v: string) { setSubject(v); setValidationError(null); }} placeholder="Agreement Review: ..." />
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.tx, marginBottom: 4 }}>Message body</div>
          <textarea value={bodyText} onChange={function(e) { setBodyText(e.target.value); }} rows={6} placeholder={attachDraft ? "Please find the attached draft for your review." : "Type your follow-up message..."} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid " + T.brd, background: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
        </div>

        {validationError && (
          <div style={{ padding: "8px 12px", background: T.ylwS, border: "1px solid " + T.ylw, borderRadius: 4, fontSize: 11, color: T.tx }}>{validationError}</div>
        )}

        {commentWarning && (
          <div style={{ padding: "10px 12px", background: T.ylwS, border: "1px solid " + T.ylw, borderRadius: 4, fontSize: 12, color: T.tx }}>
            {commentWarning.error === "comments_present" ? (
              <>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  This draft contains {commentWarning.count} Word comment{commentWarning.count === 1 ? "" : "s"} that will be visible to the counterparty.
                </div>
                <div style={{ maxHeight: 120, overflowY: "auto", marginBottom: 6 }}>
                  {commentWarning.comments.map(function(c, i) {
                    return <div key={i} style={{ fontSize: 11, color: T.txM, padding: "1px 0" }}>{(c.author || "Unknown") + ": \u201C" + c.snippet + "\u201D"}</div>;
                  })}
                  {commentWarning.count > commentWarning.comments.length && (
                    <div style={{ fontSize: 10, color: T.txD }}>{"…and " + (commentWarning.count - commentWarning.comments.length) + " more"}</div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: T.txM }}>Tracked changes are fine to send; comments usually are not. Clean the file and retry, or send anyway — the file goes exactly as-is.</div>
              </>
            ) : (
              <div style={{ fontWeight: 600 }}>Could not verify this file for comments{commentWarning.reason ? " (" + commentWarning.reason + ")" : ""} — review it manually before sending.</div>
            )}
            <div style={{ marginTop: 6 }}>
              <span onClick={function() { setCommentWarning(null); }} style={{ fontSize: 11, color: T.acc, cursor: "pointer" }}>Dismiss warning</span>
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
          <Btn v="secondary" onClick={onClose} disabled={submitting}>Cancel</Btn>
          {commentWarning ? (
            <Btn v="primary" onClick={function() { void handleSubmit(true); }} disabled={submitting}>
              {submitting ? "Sending..." : "Send anyway"}
            </Btn>
          ) : (
            <Btn v="primary" onClick={function() { void handleSubmit(); }} disabled={submitting || loadingDraft || (attachDraft && !companyDraft)}>
              {submitting ? "Sending..." : (attachDraft ? "Send draft" : "Send follow-up")}
            </Btn>
          )}
        </div>
      </div>
    </Modal>
  );
};

const ThreadView = ({ thread, currentUser, onMessagePosted, onShowToast, onNavigateToVersions }: {
  thread: MessageThread;
  currentUser: UserRow | null;
  onMessagePosted: () => void;
  onShowToast: (msg: string) => void;
  onNavigateToVersions?: () => void;
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [subscription, setSubscription] = useState<SubscriptionState>('mention_only');
  const [threadUsers, setThreadUsers] = useState<ThreadUser[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Phase 1.5: load users with thread access for the @ autocomplete
  const loadThreadUsers = useCallback(async () => {
    const res = await apiCall(`/threads/${thread.id}/users`);
    if (res && Array.isArray(res.users)) setThreadUsers(res.users);
  }, [thread.id]);

  useEffect(() => {
    void loadThreadUsers();
  }, [loadThreadUsers]);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    const res = await apiCall(`/threads/${thread.id}/messages`);
    if (res && Array.isArray(res.messages)) {
      setMessages(res.messages);
    }
    setLoading(false);
    await apiCall(`/threads/${thread.id}/read`, { method: "POST" });
  }, [thread.id]);

  const loadSubscription = useCallback(async () => {
    const res = await apiCall(`/threads/${thread.id}/subscription`);
    if (res && res.subscription) {
      setSubscription(res.subscription.state as SubscriptionState);
    }
  }, [thread.id]);

  useEffect(() => {
    void loadMessages();
    void loadSubscription();
  }, [loadMessages, loadSubscription]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSubscriptionChange = async (newState: string) => {
    const res = await apiCall(`/threads/${thread.id}/subscription`, {
      method: "PATCH",
      body: JSON.stringify({ state: newState }),
    });
    if (res) setSubscription(newState as SubscriptionState);
  };

  return (
    <Card style={{ display: "flex", flexDirection: "column", height: 500, padding: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.brd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>{thread.title}</div>
          <div style={{ fontSize: 10, color: T.txD }}>
            {THREAD_TYPE_LABELS[thread.thread_type]}
            {thread.status === 'closed' && ' · Closed'}
          </div>
        </div>
        <div style={{ minWidth: 160 }}>
          <Sel value={subscription} options={SUBSCRIPTION_OPTIONS} onChange={(v) => { void handleSubscriptionChange(v); }} />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {loading ? (
          <div style={{ textAlign: "center", color: T.txM, fontSize: 12 }}>Loading...</div>
        ) : messages.length === 0 ? (
          <EmptyState msg="No messages in this thread yet" />
        ) : (
          messages.map(m => <MessageBubble key={m.id} message={m} currentUser={currentUser} onNavigateToVersions={onNavigateToVersions} />)
        )}
        <div ref={messagesEndRef} />
      </div>

      {thread.status === 'open' ? (
        <MessageComposer
          requestId={thread.request_id}
          threadId={thread.id}
          threadUsers={threadUsers}
          onShowToast={onShowToast}
          onPosted={() => {
            void loadMessages();
            onMessagePosted();
          }}
        />
      ) : (
        <div style={{ padding: 12, textAlign: "center", color: T.txD, fontSize: 11, borderTop: `1px solid ${T.brd}` }}>
          This thread is closed
        </div>
      )}
    </Card>
  );
};

// Phase 1.5: badge for inbound replies whose sender did not match the recorded recipient
const UnverifiedSenderBadge = () => (
  <span style={{
    display: "inline-block",
    padding: "1px 6px",
    marginLeft: 6,
    fontSize: 9,
    fontWeight: 700,
    background: T.redS,
    color: T.red,
    borderRadius: 3,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  }} title="Sender email did not match the recorded recipient of the notification">
    Unverified
  </span>
);

const MessageBubble = ({ message, currentUser, onNavigateToVersions }: { message: Message; currentUser: UserRow | null; onNavigateToVersions?: () => void }) => {
  const isOwn = !!(currentUser && message.author_user_id === currentUser.id);
  const authorName = message.author_name || message.author_external_name || message.author_external_email || 'Unknown';

  // Download an attachment through the authenticated fetch→Blob pattern (2026-07-14):
  // the route is Bearer-gated, so a raw <a href> browser navigation sends no token
  // and 401s. Mirrors handleTrackedDownload above.
  const downloadAttachment = (attId: string, fallbackName: string) => {
    const token = localStorage.getItem("kontractai_token") || "";
    void (async () => {
      try {
        const resp = await fetch(`/api/message-attachments/${attId}/download`, { headers: { Authorization: "Bearer " + token } });
        if (!resp.ok) { window.alert("Download failed: HTTP " + resp.status); return; }
        const blob = await resp.blob();
        const disp = resp.headers.get("content-disposition") || "";
        // Only percent-decode the RFC5987 (filename*=UTF-8'') form; the plain
        // filename="..." form is sent un-encoded, so decodeURIComponent would
        // throw on a literal '%' (e.g. "Q1 100% Report.docx").
        const enc = disp.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
        const plain = disp.match(/filename="?([^";]+)"?/i);
        let name = fallbackName;
        if (enc) { try { name = decodeURIComponent(enc[1]); } catch { name = plain ? plain[1] : fallbackName; } }
        else if (plain) { name = plain[1]; }
        const url = URL.createObjectURL(blob);
        const el = document.createElement("a");
        el.href = url; el.download = name;
        document.body.appendChild(el); el.click(); document.body.removeChild(el);
        URL.revokeObjectURL(url);
      } catch (e) { window.alert("Download failed."); }
    })();
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: T.txD, marginBottom: 2, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
        <strong>{authorName}</strong>
        <OrgBadge org_type={(message as any).author_org_type} size="xs" />
        {message.inbound_sender_verified === false && <UnverifiedSenderBadge />}
        <span>· {fmtDate(message.created_at)}{message.is_edited ? ' · edited' : ''}</span>
      </div>
      <div style={{
        padding: "8px 12px",
        borderRadius: 8,
        background: isOwn ? T.accS : T.bg3,
        fontSize: 12,
        color: T.tx,
        whiteSpace: "pre-wrap",
      }}>
        {/* De-duped 2026-07-23 (owner ruling): the bold inline "[Attachment]
            Imported as v…" link duplicated the attachment
            file chip below — the chip is the ONE link for inbound AND
            outbound (the sent draft now records a real attachment too). */}
        {message.body_text || ''}
      </div>
      {message.attachments && message.attachments.length > 0 && (
        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {message.attachments.map(a => (
            <span
              key={a.id}
              role="button"
              tabIndex={0}
              onClick={() => downloadAttachment(a.id, a.original_filename)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); downloadAttachment(a.id, a.original_filename); } }}
              style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, border: `1px solid ${T.brd}`, color: T.acc, textDecoration: "none", cursor: "pointer" }}
            >
              {a.original_filename} ({Math.round(a.file_size_bytes / 1024)} KB)
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Phase 1.5: composer with @ autocomplete + warning toast handling
const MessageComposer = ({ requestId, threadId, threadUsers, onShowToast, onPosted }: {
  requestId: string;
  threadId: string;
  threadUsers: ThreadUser[];
  onShowToast: (msg: string) => void;
  onPosted: () => void;
}) => {
  const [body, setBody] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [posting, setPosting] = useState<boolean>(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStartIdx, setMentionStartIdx] = useState<number>(-1);
  const [selectedMentionIdx, setSelectedMentionIdx] = useState<number>(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredUsers = mentionQuery !== null
    ? threadUsers.filter(u => {
        const q = mentionQuery.toLowerCase();
        return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      }).slice(0, 6)
    : [];

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newBody = e.target.value;
    setBody(newBody);

    const cursor = e.target.selectionStart || 0;
    const before = newBody.substring(0, cursor);
    const atIdx = before.lastIndexOf('@');
    if (atIdx >= 0) {
      const charBefore = atIdx === 0 ? ' ' : before.charAt(atIdx - 1);
      if (charBefore === ' ' || charBefore === '\n' || atIdx === 0) {
        const query = before.substring(atIdx + 1);
        if (!/\s/.test(query)) {
          setMentionQuery(query.toLowerCase());
          setMentionStartIdx(atIdx);
          setSelectedMentionIdx(0);
          return;
        }
      }
    }
    setMentionQuery(null);
    setMentionStartIdx(-1);
  };

  const insertMention = (user: ThreadUser) => {
    if (mentionStartIdx < 0) return;
    const before = body.substring(0, mentionStartIdx);
    const cursor = textareaRef.current?.selectionStart || body.length;
    const after = body.substring(cursor);
    const handle = user.name.split(' ')[0] || user.email.split('@')[0];
    const newBody = `${before}@${handle} ${after}`;
    setBody(newBody);
    setMentionQuery(null);
    setMentionStartIdx(-1);
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + handle.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIdx(i => Math.min(i + 1, filteredUsers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIdx(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredUsers[selectedMentionIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        setMentionStartIdx(-1);
        return;
      }
    }
  };

  const handleSend = async () => {
    if (!body.trim()) return;
    setPosting(true);
    const res = await apiCall(`/requests/${requestId}/messages`, {
      method: "POST",
      body: JSON.stringify({ thread_id: threadId, body_text: body }),
    });
    if (res && res.message && files.length > 0) {
      const formData = new FormData();
      for (const f of files) formData.append('attachments', f);
      await apiCall(`/messages/${res.message.id}/attachments`, {
        method: "POST",
        body: formData,
      });
    }
    if (res) {
      setBody("");
      setFiles([]);
      onPosted();
      if (res.warnings && Array.isArray(res.warnings) && res.warnings.length > 0) {
        const msgs = res.warnings.map((w: MentionWarning) => w.message).join('. ');
        onShowToast(msgs);
      }
    }
    setPosting(false);
  };

  return (
    <div style={{ borderTop: `1px solid ${T.brd}`, padding: 12, position: "relative" }}>
      <textarea
        ref={textareaRef}
        value={body}
        onChange={handleBodyChange}
        onKeyDown={handleKeyDown}
        placeholder="Write a message... use @ to mention someone"
        rows={3}
        style={{
          width: "100%",
          padding: 8,
          fontSize: 12,
          borderRadius: 4,
          border: `1px solid ${T.brd}`,
          resize: "vertical",
          fontFamily: "inherit",
          boxSizing: "border-box",
        }}
      />
      {mentionQuery !== null && filteredUsers.length > 0 && (
        <div style={{
          position: "absolute",
          bottom: 90,
          left: 12,
          background: T.bg2,
          border: `1px solid ${T.brd}`,
          borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          minWidth: 240,
          maxHeight: 200,
          overflowY: "auto",
          zIndex: 100,
        }}>
          {filteredUsers.map((u, i) => (
            <div
              key={u.id}
              onClick={() => insertMention(u)}
              onMouseEnter={() => setSelectedMentionIdx(i)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                background: i === selectedMentionIdx ? T.accS : "transparent",
                fontSize: 12,
                borderBottom: `1px solid ${T.brd}`,
              }}
            >
              <div style={{ fontWeight: 600, color: T.tx }}>{u.name}</div>
              <div style={{ fontSize: 10, color: T.txD }}>{u.email} · {u.role_slug}</div>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 10, color: T.txM }}>
          {files.map((f, i) => <div key={i}>{f.name} ({Math.round(f.size / 1024)} KB)</div>)}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={e => {
              const list = e.target.files;
              if (list) setFiles(Array.from(list).slice(0, 5));
            }}
          />
          <Btn v="ghost" sz="sm" onClick={() => fileInputRef.current?.click()}>Attach</Btn>
        </div>
        <Btn v="primary" sz="sm" onClick={() => { void handleSend(); }} disabled={posting || !body.trim()}>
          {posting ? "Posting..." : "Send"}
        </Btn>
      </div>
    </div>
  );
};

const VISIBILITY_PRESET_OPTIONS = [
  { value: 'legal_only', label: 'Legal only' },
  { value: 'commops_only', label: 'CommOps only' },
  { value: 'legal_plus_commops', label: 'Legal + CommOps' },
  { value: 'executive_briefing', label: 'Executive briefing' },
];

const AdHocThreadCreateModal = ({ requestId, onClose, onCreated }: { requestId: string; onClose: () => void; onCreated: () => void }) => {
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [preset, setPreset] = useState<string>("legal_plus_commops");
  const [creating, setCreating] = useState<boolean>(false);

  const handleCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    const res = await apiCall(`/requests/${requestId}/threads`, {
      method: "POST",
      body: JSON.stringify({
        title,
        description,
        thread_type: 'ad_hoc',
        visibility_preset: preset,
      }),
    });
    setCreating(false);
    if (res) onCreated();
  };

  return (
    <Modal open onClose={onClose} title="Create ad hoc thread">
      <div style={{ marginBottom: 12 }}>
        <Inp label="Title" value={title} onChange={setTitle} placeholder="e.g., Indemnification strategy" />
      </div>
      <div style={{ marginBottom: 12 }}>
        <Inp label="Description (optional)" value={description} onChange={setDescription} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <Sel label="Visibility" value={preset} options={VISIBILITY_PRESET_OPTIONS} onChange={setPreset} />
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn v="secondary" onClick={onClose}>Cancel</Btn>
        <Btn v="primary" onClick={() => { void handleCreate(); }} disabled={creating || !title.trim()}>
          {creating ? "Creating..." : "Create"}
        </Btn>
      </div>
    </Modal>
  );
};

// ============================================================================
// Phase 2: ThreadSummaryPanel
// ============================================================================
const ThreadSummaryPanel = ({ threadId }: { threadId: string }) => {
  const [summary, setSummary] = useState<ThreadSummary | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [streaming, setStreaming] = useState<boolean>(false);
  const [streamedText, setStreamedText] = useState<string>("");
  const [expanded, setExpanded] = useState<boolean>(false);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    const res = await apiCall(`/threads/${threadId}/summary`);
    if (res) {
      setSummary(res.summary as ThreadSummary | null);
      setIsStale(!!res.is_stale);
    }
    setLoading(false);
  }, [threadId]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const handleGenerate = async () => {
    setStreaming(true);
    setStreamedText("");
    // No credential in this URL. The stream cookie is established over a
    // header first (streamSession.ts); the browser attaches it to the stream.
    await ensureStreamSession();
    const url = `/api/threads/${threadId}/summarize/stream`;
    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.addEventListener("start", () => {
      setStreamedText("");
    });
    eventSource.addEventListener("delta", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (data.text) setStreamedText(prev => prev + data.text);
      } catch { /* ignore */ }
    });
    eventSource.addEventListener("done", () => {
      setStreaming(false);
      eventSource.close();
      void loadSummary();
    });
    eventSource.addEventListener("error", (e: MessageEvent) => {
      let errorMsg = "Summary generation failed";
      try {
        const data = JSON.parse((e as any).data || "{}");
        if (data.error) errorMsg = data.error;
      } catch { /* ignore */ }
      setStreamedText(prev => prev + `\n\n[Error: ${errorMsg}]`);
      setStreaming(false);
      eventSource.close();
    });
  };

  const handlePin = async () => {
    if (!summary) return;
    const res = await apiCall(`/thread-summaries/${summary.id}/pin`, { method: "POST" });
    if (res) void loadSummary();
  };

  if (loading) {
    return (
      <div style={{ padding: 12, background: T.bg3, borderRadius: 6, fontSize: 11, color: T.txM }}>
        Loading summary...
      </div>
    );
  }

  const displayText = streaming ? streamedText : (summary?.summary_text || "");
  const hasContent = streaming || !!summary;

  return (
    <div style={{
      padding: "10px 14px",
      background: summary?.is_pinned ? T.accS : T.bg3,
      borderRadius: 6,
      marginBottom: 8,
      borderLeft: summary?.is_pinned ? `3px solid ${T.acc}` : `3px solid ${T.brd}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.tx, textTransform: "uppercase", letterSpacing: "0.03em" }}>
            {summary?.is_pinned ? "Pinned Summary" : "Summary"}
          </span>
          {summary && (
            <span style={{ fontSize: 10, color: T.txD }}>
              generated {fmtDate(summary.generated_at)}
            </span>
          )}
          {isStale && !streaming && (
            <span style={{ fontSize: 9, padding: "1px 6px", background: T.ylwS, color: T.ylw, borderRadius: 3, fontWeight: 700, textTransform: "uppercase" }}>
              Stale
            </span>
          )}
          {streaming && (
            <span style={{ fontSize: 9, padding: "1px 6px", background: T.grnS, color: T.grn, borderRadius: 3, fontWeight: 700, textTransform: "uppercase" }}>
              Generating...
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {!streaming && (
            <Btn v="ghost" sz="sm" onClick={handleGenerate}>
              {summary ? (isStale ? "Refresh" : "Regenerate") : "Generate"}
            </Btn>
          )}
          {summary && !summary.is_pinned && !streaming && (
            <Btn v="ghost" sz="sm" onClick={() => { void handlePin(); }}>Pin</Btn>
          )}
        </div>
      </div>
      {hasContent ? (
        <div style={{
          fontSize: 12,
          color: T.tx,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          maxHeight: expanded ? undefined : 120,
          overflow: "hidden",
          position: "relative",
        }}>
          {displayText}
          {!expanded && displayText.length > 300 && !streaming && (
            <div style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 40,
              background: `linear-gradient(to bottom, transparent, ${summary?.is_pinned ? T.accS : T.bg3})`,
              pointerEvents: "none",
            }} />
          )}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: T.txD, fontStyle: "italic" }}>
          No summary yet. Click Generate to create one.
        </div>
      )}
      {hasContent && displayText.length > 300 && !streaming && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: "none",
            border: "none",
            color: T.acc,
            fontSize: 10,
            fontWeight: 600,
            cursor: "pointer",
            padding: "4px 0",
            fontFamily: "inherit",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

// ============================================================================
// Phase 2: ScopedChatDrawer
// ============================================================================
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
}

// Phase 2 UX refinement: typing dots animation for "waiting for first token"
const TypingDots = () => {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 0" }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: T.txD, animation: "kontractai-pulse 1.4s ease-in-out infinite", animationDelay: "0s" }} />
      <span style={{ width: 6, height: 6, borderRadius: 3, background: T.txD, animation: "kontractai-pulse 1.4s ease-in-out infinite", animationDelay: "0.2s" }} />
      <span style={{ width: 6, height: 6, borderRadius: 3, background: T.txD, animation: "kontractai-pulse 1.4s ease-in-out infinite", animationDelay: "0.4s" }} />
      <style>
        {`
          @keyframes kontractai-pulse {
            0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
            30% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
};

// Phase 2 UX refinement: chat message bubble with three states (thinking / streaming / done)
const ChatMessageBubble = ({ message }: { message: ChatMessage }) => {
  const isUser = message.role === 'user';
  const isWaitingForFirstToken = message.streaming && !message.content;
  const isStreaming = message.streaming && !!message.content;
  const isDone = !message.streaming && !!message.content;

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 9,
        color: T.txD,
        marginBottom: 3,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}>
        <span>{isUser ? 'You' : 'Assistant'}</span>
        {isWaitingForFirstToken && (
          <span style={{ color: T.txD, fontSize: 9, fontStyle: "italic", textTransform: "none" }}>
            thinking...
          </span>
        )}
        {isStreaming && (
          <span style={{ color: T.grn, fontSize: 9, fontStyle: "italic", textTransform: "none" }}>
            streaming...
          </span>
        )}
        {isDone && !isUser && (
          <span style={{ color: T.grn, fontSize: 10, fontWeight: 700 }} title="Response complete">
            ✓
          </span>
        )}
      </div>
      <div style={{
        padding: "10px 14px",
        borderRadius: 6,
        background: isUser ? T.accS : T.bg3,
        fontSize: 12,
        color: T.tx,
        whiteSpace: "pre-wrap",
        lineHeight: 1.5,
        minHeight: isWaitingForFirstToken ? 36 : undefined,
        display: "flex",
        alignItems: "center",
      }}>
        {isWaitingForFirstToken ? (
          <TypingDots />
        ) : (
          <span>{message.content}</span>
        )}
      </div>
    </div>
  );
};

const ScopedChatDrawer = ({ requestId, selectedThreadId, currentUserId, open, onClose }: { requestId: string; selectedThreadId: string | null; currentUserId: string | null; open: boolean; onClose: () => void }) => {
  const [question, setQuestion] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!question.trim() || streaming) return;
    const userMsg: ChatMessage = { role: 'user', content: question };
    const assistantMsg: ChatMessage = { role: 'assistant', content: '', streaming: true };
    setMessages(prev => [...prev, userMsg, assistantMsg]);
    const q = question;
    setQuestion("");
    setStreaming(true);

    // The CREDENTIAL leaves this URL (as above). `q` — the user's actual
    // question — DOES NOT, and is still written into access logs. That is a
    // real, separate privacy problem and it is NOT fixed here: EventSource
    // cannot send a header or a body, so moving `q` needs a POST-then-stream
    // handshake (post the question, receive a short-lived id, stream that id).
    // Deliberately left as a named follow-up rather than half-done, so nobody
    // reads this line as though the query were already private.
    await ensureStreamSession();
    const url = `/api/requests/${requestId}/chat/stream?q=${encodeURIComponent(q)}`;
    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.addEventListener("delta", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (data.text) {
          setMessages(prev => {
            const copy = [...prev];
            const last = copy[copy.length - 1];
            if (last && last.role === 'assistant') {
              copy[copy.length - 1] = { ...last, content: last.content + data.text };
            }
            return copy;
          });
        }
      } catch { /* ignore */ }
    });
    eventSource.addEventListener("done", () => {
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant') {
          copy[copy.length - 1] = { ...last, streaming: false };
        }
        return copy;
      });
      setStreaming(false);
      eventSource.close();
    });
    eventSource.addEventListener("error", (e: MessageEvent) => {
      let errorMsg = "Query failed";
      try {
        const data = JSON.parse((e as any).data || "{}");
        if (data.error) errorMsg = data.error;
      } catch { /* ignore */ }
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant') {
          copy[copy.length - 1] = { ...last, content: last.content + `\n\n[Error: ${errorMsg}]`, streaming: false };
        }
        return copy;
      });
      setStreaming(false);
      eventSource.close();
    });
  };

  if (!open) return null;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      width: 480,
      background: T.bg2,
      borderLeft: `1px solid ${T.brd}`,
      boxShadow: "-4px 0 16px rgba(0,0,0,0.1)",
      zIndex: 9000,
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.brd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Ask about this request</div>
          <div style={{ fontSize: 10, color: T.txD }}>Answers draw only from threads you can see</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, color: T.txM, cursor: "pointer" }}>×</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {/* Summary section: top */}
        {selectedThreadId && (
          <div style={{ padding: "14px 14px 10px 14px", borderBottom: `1px solid ${T.brd}` }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
              Thread Summary
            </div>
            <ThreadSummaryPanel threadId={selectedThreadId} />
          </div>
        )}

        {/* Chat section: bottom */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
            Ask a Question
          </div>
          {messages.length === 0 ? (
            <div style={{ padding: 10, color: T.txD, fontSize: 11, fontStyle: "italic", lineHeight: 1.5 }}>
              Ask a question about the threads on this request. Examples: "What did we decide about indemnification?" or "Has anyone raised concerns about the timeline?"
            </div>
          ) : (
            messages.map((m, i) => (
              <ChatMessageBubble key={i} message={m} />
            ))
          )}
        </div>
      </div>
      <div style={{ padding: 12, borderTop: `1px solid ${T.brd}` }}>
        <textarea
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Ask a question..."
          rows={2}
          disabled={streaming}
          style={{
            width: "100%",
            padding: 8,
            fontSize: 12,
            borderRadius: 4,
            border: `1px solid ${T.brd}`,
            resize: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
            opacity: streaming ? 0.6 : 1,
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <Btn v="primary" sz="sm" onClick={handleSend} disabled={streaming || !question.trim()}>
            {streaming ? "Thinking..." : "Ask"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

export default CommunicationsTab;
